<template>
  <div class="page-content">
    <div class="page-header">
      <div>
        <BreadcrumbComponent :items="items" />
        <h1 class="page-title">Mouvements de Stock</h1>
        <p class="page-subtitle" v-if="product">{{ product.name }} — {{ product.category || '—' }}</p>
      </div>
      <div class="d-flex" style="gap: 8px;" v-if="movements.length">
        <v-btn color="error" outlined small @click="exportPDF"><v-icon left small>mdi-file-pdf-box</v-icon>PDF</v-btn>
        <v-btn color="success" outlined small @click="exportExcel"><v-icon left small>mdi-file-excel</v-icon>Excel</v-btn>
      </div>
    </div>

    <!-- Product selector -->
    <v-card flat class="filter-card mb-4">
      <v-card-text class="py-3">
        <v-row align="center" dense>
          <v-col cols="12" sm="6" md="4">
            <v-autocomplete
              v-model="selectedProductId"
              :items="allProducts"
              item-text="name"
              item-value="id"
              placeholder="Sélectionner un produit..."
              outlined
              dense
              hide-details
              clearable
              prepend-inner-icon="mdi-magnify"
              :filter="filterProducts"
              @change="loadMovements"
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" sm="4" md="2">
            <v-select
              v-model="store"
              :items="stores"
              item-text="name"
              item-value="id"
              outlined dense hide-details clearable
              placeholder="Tous les emplacements"
              prepend-inner-icon="mdi-store-outline"
              @change="loadMovements"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="6" md="auto">
            <v-btn color="primary" depressed small @click="loadMovements" :loading="loading" :disabled="!selectedProductId">
              <v-icon left small>mdi-check</v-icon> Charger
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Loading -->
    <div v-if="loading" class="page-loader">
      <v-progress-circular indeterminate color="primary" size="40" width="3"></v-progress-circular>
    </div>

    <div v-if="product && !loading">
      <!-- Summary cards -->
      <div class="summary-grid mb-2">
        <div class="summary-cell">
          <v-card flat class="summary-card">
            <div class="summary-icon-sm" style="background:#194173"><v-icon color="white" size="18">mdi-arrow-left-bold</v-icon></div>
            <div class="summary-val">{{ openingBalance }}</div>
            <div class="summary-lbl">Stock Initial</div>
          </v-card>
        </div>
        <div class="summary-cell">
          <v-card flat class="summary-card">
            <div class="summary-icon-sm" style="background:#4CAF50"><v-icon color="white" size="18">mdi-plus</v-icon></div>
            <div class="summary-val success--text">+{{ summary.total_receivings }}</div>
            <div class="summary-lbl">Réceptions</div>
          </v-card>
        </div>
        <div class="summary-cell">
          <v-card flat class="summary-card">
            <div class="summary-icon-sm" style="background:#F44336"><v-icon color="white" size="18">mdi-minus</v-icon></div>
            <div class="summary-val error--text">-{{ summary.total_sales }}</div>
            <div class="summary-lbl">Ventes</div>
          </v-card>
        </div>
        <div class="summary-cell" v-if="summary.total_returns">
          <v-card flat class="summary-card">
            <div class="summary-icon-sm" style="background:#f9a825"><v-icon color="white" size="18">mdi-keyboard-return</v-icon></div>
            <div class="summary-val" style="color:#f9a825">+{{ summary.total_returns }}</div>
            <div class="summary-lbl">Retours</div>
          </v-card>
        </div>
        <div class="summary-cell">
          <v-card flat class="summary-card">
            <div class="summary-icon-sm" style="background:#f68d3b"><v-icon color="white" size="18">mdi-arrow-right-bold</v-icon></div>
            <div class="summary-val" :class="closingBalance < 0 ? 'error--text' : ''">{{ closingBalance }}</div>
            <div class="summary-lbl">Stock Final</div>
          </v-card>
        </div>
      </div>

      <!-- Movements table -->
      <v-card flat class="table-card">
        <div class="table-responsive">
          <table class="report-table">
            <thead>
              <tr>
                <th class="text-left">DATE</th>
                <th class="text-center">TYPE</th>
                <th class="text-left">RÉFÉRENCE</th>
                <th class="text-left">UTILISATEUR</th>
                <th class="text-right">QUANTITÉ</th>
                <th class="text-right">SOLDE</th>
                <th class="text-left">NOTE</th>
              </tr>
              <!-- Opening balance row -->
              <tr class="opening-row">
                <th colspan="4" class="text-left">SOLDE D'OUVERTURE</th>
                <th></th>
                <th class="text-right">{{ openingBalance }}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(m, idx) in movements" :key="idx">
                <td class="text-left text-caption">{{ fmtDate(m.date) }} {{ fmtTime(m.date) }}</td>
                <td class="text-center">
                  <v-chip x-small :color="getTypeColor(m.type)" dark>{{ m.type_label }}</v-chip>
                </td>
                <td class="text-left text-caption">{{ m.reference || '—' }}</td>
                <td class="text-left text-caption">{{ m.user || '—' }}</td>
                <td class="text-right font-weight-bold" :class="m.type === 'return' ? 'amber--text text--darken-2' : (m.quantity > 0 ? 'success--text' : 'error--text')">
                  {{ m.quantity > 0 ? '+' : '' }}{{ m.quantity }}
                </td>
                <td class="text-right font-weight-medium" :class="m.balance < 0 ? 'error--text' : ''">{{ m.balance }}</td>
                <td class="text-left text-caption text--secondary">{{ m.memo || '—' }}</td>
              </tr>
              <tr v-if="movements.length === 0">
                <td colspan="7" class="text-center text--secondary pa-6">Aucun mouvement pour cette période</td>
              </tr>
            </tbody>
            <tfoot v-if="movements.length > 0">
              <tr class="closing-row">
                <td colspan="4" class="text-left font-weight-bold">SOLDE DE CLÔTURE</td>
                <td></td>
                <td class="text-right font-weight-bold" :class="closingBalance < 0 ? 'error--text' : ''">{{ closingBalance }}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </v-card>
    </div>

    <!-- Initial state -->
    <v-card flat v-if="!product && !loading && !loadingProducts" class="empty-card">
      <v-card-text class="text-center py-12">
        <v-icon size="56" color="grey lighten-2">mdi-swap-vertical</v-icon>
        <h3 class="text-body-1 font-weight-medium mt-4 mb-1">Sélectionnez un produit</h3>
        <p class="text-caption text--secondary">Choisissez un produit ci-dessus pour voir tous ses mouvements de stock</p>
      </v-card-text>
    </v-card>

    <!-- Preload -->
    <div v-if="loadingProducts" class="page-loader">
      <div class="text-center">
        <v-progress-circular indeterminate color="primary" size="40" width="3" class="mb-3"></v-progress-circular>
        <p class="text-body-2 text--secondary">Chargement des produits...</p>
      </div>
    </div>
  </div>
</template>

<script>
import BreadcrumbComponent from '../components/BreadcrumbComponent.vue'

export default {
  name: 'StockMovementView',
  components: { BreadcrumbComponent },
  data: () => ({
    items: [{ text: 'Mouvements Stock', disabled: true, href: '/stock-movements' }],
    allProducts: [],
    loadingProducts: true,
    selectedProductId: null,
    stores: [],
    store: null,
    loading: false,
    product: null,
    movements: [],
    openingBalance: 0,
    closingBalance: 0,
    summary: { total_sales: 0, total_returns: 0, total_receivings: 0, total_adjustments: 0 },
  }),

  methods: {
    filterProducts(item, queryText) {
      var q = (queryText || '').toLowerCase()
      return (item.name && item.name.toLowerCase().indexOf(q) !== -1) ||
             (item.barcode && item.barcode.toLowerCase().indexOf(q) !== -1)
    },

    fmtDate(d) { if (!d) return '—'; return new Date(d).toLocaleDateString('fr-FR') },
    fmtTime(d) { if (!d) return ''; return new Date(d).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) },
    fmt(v) {
      if (!v && v !== 0) return '0.00'
      var p = Math.abs(parseFloat(v)).toFixed(2).split('.')
      p[0] = p[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
      return (parseFloat(v) < 0 ? '-' : '') + p.join('.')
    },

    getTypeColor(type) {
      if (type === 'sale') return 'error'
      if (type === 'receiving') return 'success'
      if (type === 'return') return 'amber darken-2'
      return 'info'
    },

    getDateParams() {
      var isValid = function (v) { return v && v !== 'null' && v.trim() !== '' }
      return {
        from: isValid(localStorage.debut_periode) ? localStorage.debut_periode : '',
        to: isValid(localStorage.fin_periode) ? localStorage.fin_periode : ''
      }
    },

    async preloadProducts() {
      this.loadingProducts = true
      try {
        var r = await this.$axios.get('/products?orderasc=name')
        this.allProducts = r.data || []
      } catch (e) { /* ignore */ }
      try {
        var biz = JSON.parse(localStorage.business)
        var s = await this.$axios.get('/stores?orderasc=name&business_id=' + biz.id)
        this.stores = s.data || []
      } catch (e) { /* ignore */ }
      this.loadingProducts = false
    },

    async loadMovements() {
      if (!this.selectedProductId) return
      this.loading = true
      this.product = null
      this.movements = []

      try {
        var biz = JSON.parse(localStorage.business)
        var d = this.getDateParams()
        var url = '/businesses/stock-movements/' + biz.id + '?product_id=' + this.selectedProductId + '&from=' + d.from + '&to=' + d.to
        if (this.store) url += '&store_id=' + this.store
        var r = await this.$axios.get(url)

        if (r.data.success) {
          this.product = r.data.data.product
          this.movements = r.data.data.movements
          this.openingBalance = r.data.data.opening_balance
          this.closingBalance = r.data.data.closing_balance
          this.summary = r.data.data.summary
        }
      } catch (e) {
        console.error('Stock movements error:', e)
      }
      this.loading = false
    },

    async exportPDF() {
      var self = this
      var { jsPDF } = await import('jspdf')
      var doc = new jsPDF('l', 'mm', 'a4')
      var pw = 297, ph = 210, ml = 12, mr = pw - 12, cw = mr - ml
      var biz = {}; try { biz = JSON.parse(localStorage.business) || {} } catch (e) { biz = {} }
      var primary = [25, 65, 115], dark = [30, 41, 59], muted = [100, 116, 139], border = [220, 225, 230], light = [248, 250, 252]
      var d = self.getDateParams()

      // Header
      doc.setFillColor(...primary); doc.rect(0, 0, pw, 22, 'F')
      doc.setFontSize(12); doc.setFont('helvetica', 'bold'); doc.setTextColor(255, 255, 255)
      doc.text('MOUVEMENTS DE STOCK', ml, 10)
      doc.setFontSize(9); doc.setTextColor(200, 215, 235)
      doc.text(self.product.name, ml, 17)
      doc.setFontSize(8); doc.setFont('helvetica', 'normal')
      doc.text(d.from + ' — ' + d.to, mr, 8, { align: 'right' })
      if (biz.name) doc.text(biz.name, mr, 14, { align: 'right' })

      var y = 28

      // Summary line
      doc.setFontSize(8); doc.setFont('helvetica', 'bold'); doc.setTextColor(...dark)
      var retStr = self.summary.total_returns ? '   |   Retours: +' + self.summary.total_returns : ''
      doc.text('Ouverture: ' + self.openingBalance + '   |   Réceptions: +' + self.summary.total_receivings + '   |   Ventes: -' + self.summary.total_sales + retStr + '   |   Clôture: ' + self.closingBalance, ml, y)
      y += 8

      // Table
      var cols = [
        { label: 'DATE', w: 36, align: 'left' },
        { label: 'TYPE', w: 22, align: 'center' },
        { label: 'RÉFÉRENCE', w: 30, align: 'left' },
        { label: 'UTILISATEUR', w: 30, align: 'left' },
        { label: 'QTÉ', w: 18, align: 'right' },
        { label: 'SOLDE', w: 18, align: 'right' },
        { label: 'NOTE', w: 40, align: 'left' },
      ]
      var totalW = cols.reduce(function (s, c) { return s + c.w }, 0)
      var scale = cw / totalW; cols.forEach(function (c) { c.w = Math.round(c.w * scale) })
      var actualW = cols.reduce(function (s, c) { return s + c.w }, 0); cols[6].w += (cw - actualW)
      var cx = ml; cols.forEach(function (c) { c.x = cx; cx += c.w })
      var rowH = 6.5

      function drawHeader() {
        doc.setFillColor(...primary); doc.rect(ml, y, cw, rowH + 1, 'F')
        doc.setFontSize(6); doc.setFont('helvetica', 'bold'); doc.setTextColor(255, 255, 255)
        cols.forEach(function (c) { var tx = c.align === 'right' ? c.x + c.w - 3 : (c.align === 'center' ? c.x + c.w / 2 : c.x + 3); doc.text(c.label, tx, y + 4.5, { align: c.align }) })
        y += rowH + 1
      }

      // Opening balance row
      doc.setFillColor(...light); doc.rect(ml, y, cw, rowH, 'F')
      doc.setFontSize(7); doc.setFont('helvetica', 'bold'); doc.setTextColor(...dark)
      doc.text('SOLDE D\'OUVERTURE', ml + 3, y + 4.2)
      doc.text(String(self.openingBalance), cols[5].x + cols[5].w - 3, y + 4.2, { align: 'right' })
      doc.setDrawColor(...border); doc.setLineWidth(0.2); doc.line(ml, y + rowH, mr, y + rowH)
      y += rowH

      drawHeader()

      self.movements.forEach(function (m, idx) {
        if (y + rowH > ph - 22) { doc.addPage(); y = 12; drawHeader() }
        if (idx % 2 === 0) { doc.setFillColor(250, 251, 253); doc.rect(ml, y, cw, rowH, 'F') }
        doc.setDrawColor(...border); doc.setLineWidth(0.1); doc.line(ml, y + rowH, mr, y + rowH)
        doc.setFontSize(7); doc.setFont('helvetica', 'normal'); doc.setTextColor(...dark)

        var memo = m.memo || ''; if (memo.length > 24) memo = memo.substring(0, 22) + '..'
        var ref = m.reference || ''; if (ref.length > 16) ref = ref.substring(0, 14) + '..'
        var user = m.user || ''; if (user.length > 16) user = user.substring(0, 14) + '..'
        var qtyStr = (m.quantity > 0 ? '+' : '') + m.quantity
        var vals = [self.fmtDate(m.date) + ' ' + self.fmtTime(m.date), m.type_label, ref, user, qtyStr, String(m.balance), memo]

        cols.forEach(function (c, i) {
          var tx = c.align === 'right' ? c.x + c.w - 3 : (c.align === 'center' ? c.x + c.w / 2 : c.x + 3)
          if (i === 4) { if (m.type === 'return') { doc.setTextColor(249, 168, 37) } else { doc.setTextColor(m.quantity > 0 ? 22 : 220, m.quantity > 0 ? 163 : 38, m.quantity > 0 ? 74 : 38) } }
          else if (i === 5 && m.balance < 0) { doc.setTextColor(220, 38, 38) }
          else { doc.setTextColor(...dark) }
          doc.text(vals[i], tx, y + 4.2, { align: c.align })
        })
        y += rowH
      })

      // Closing balance
      y += 1; doc.setFillColor(...light); doc.rect(ml, y, cw, rowH + 1, 'F')
      doc.setDrawColor(...primary); doc.setLineWidth(0.4); doc.line(ml, y, mr, y)
      doc.setFontSize(7); doc.setFont('helvetica', 'bold'); doc.setTextColor(...primary)
      doc.text('SOLDE DE CLÔTURE', ml + 3, y + 4.8)
      doc.text(String(self.closingBalance), cols[5].x + cols[5].w - 3, y + 4.8, { align: 'right' })

      // Footer
      var totalPages = doc.internal.getNumberOfPages()
      for (var p = 1; p <= totalPages; p++) {
        doc.setPage(p); doc.setDrawColor(...border); doc.setLineWidth(0.2); doc.line(ml, ph - 10, mr, ph - 10)
        doc.setFontSize(6); doc.setFont('helvetica', 'bold'); doc.setTextColor(...primary); doc.text('KES LAKAY', ml, ph - 6)
        doc.setFont('helvetica', 'normal'); doc.setTextColor(...muted); doc.text('Page ' + p + ' / ' + totalPages, pw / 2, ph - 6, { align: 'center' })
        var now = new Date(); doc.text(now.toLocaleDateString('fr-FR') + ' ' + now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }), mr, ph - 6, { align: 'right' })
      }
      doc.save('mouvements_stock_' + self.product.name.replace(/\s+/g, '_') + '.pdf')
    },

    exportExcel() {
      var self = this
      var XLSX = require('xlsx')
      var biz = {}; try { biz = JSON.parse(localStorage.business) || {} } catch (e) { biz = {} }
      var d = self.getDateParams()
      var wsData = [
        ['MOUVEMENTS DE STOCK — ' + self.product.name],
        [biz.name || ''],
        ['Du ' + d.from + ' au ' + d.to],
        [],
        self.summary.total_returns
          ? ['Ouverture: ' + self.openingBalance, 'Réceptions: +' + self.summary.total_receivings, 'Ventes: -' + self.summary.total_sales, 'Retours: +' + self.summary.total_returns, 'Clôture: ' + self.closingBalance]
          : ['Ouverture: ' + self.openingBalance, '', 'Réceptions: +' + self.summary.total_receivings, '', 'Ventes: -' + self.summary.total_sales, '', 'Clôture: ' + self.closingBalance],
        [],
        ['Date', 'Type', 'Référence', 'Utilisateur', 'Quantité', 'Solde', 'Note'],
      ]
      self.movements.forEach(function (m) {
        wsData.push([
          self.fmtDate(m.date) + ' ' + self.fmtTime(m.date),
          m.type_label, m.reference || '', m.user || '',
          m.quantity, m.balance, m.memo || ''
        ])
      })
      wsData.push([]); wsData.push(['SOLDE DE CLÔTURE', '', '', '', '', self.closingBalance, ''])

      var wb = XLSX.utils.book_new(); var ws = XLSX.utils.aoa_to_sheet(wsData)
      ws['!cols'] = [{ wch: 20 }, { wch: 14 }, { wch: 18 }, { wch: 18 }, { wch: 10 }, { wch: 10 }, { wch: 28 }]
      ws['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { r: 6, c: 0 }, e: { r: 6, c: 6 } }) }
      ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 5 } }, { s: { r: 1, c: 0 }, e: { r: 1, c: 5 } }, { s: { r: 2, c: 0 }, e: { r: 2, c: 5 } }]
      XLSX.utils.book_append_sheet(wb, ws, 'Mouvements')
      XLSX.writeFile(wb, 'mouvements_stock_' + self.product.name.replace(/\s+/g, '_') + '.xlsx')
    },
  },

  mounted() { this.preloadProducts() },
}
</script>

<style scoped>
.summary-grid { display: flex; flex-wrap: wrap; gap: 8px; }
.summary-cell { flex: 1 1 0; min-width: 130px; }
.summary-card { border: 1px solid #e8ecf1 !important; border-radius: 10px !important; box-shadow: 0 1px 3px rgba(0,0,0,0.04) !important; padding: 16px; text-align: center; }
.summary-icon-sm { width: 36px; height: 36px; border-radius: 8px; display: inline-flex; align-items: center; justify-content: center; margin-bottom: 8px; }
.summary-val { font-size: 1.125rem; font-weight: 700; color: #1a2332; }
.summary-lbl { font-size: 0.75rem; color: #64748b; margin-top: 2px; }
.filter-card { border: 1px solid #e8ecf1 !important; border-radius: 8px !important; box-shadow: 0 1px 3px rgba(0,0,0,0.04) !important; }
.table-card { border: 1px solid #e8ecf1 !important; border-radius: 8px !important; box-shadow: 0 1px 3px rgba(0,0,0,0.04) !important; overflow: hidden; }
.table-responsive { overflow-x: auto; max-height: 600px; overflow-y: auto; }
.report-table { width: 100%; border-collapse: collapse; font-size: 0.8125rem; }
.report-table thead th { position: sticky; top: 0; background: #f8fafc; font-weight: 600; font-size: 0.6875rem; text-transform: uppercase; letter-spacing: 0.05em; color: #64748b; padding: 10px 12px; border-bottom: 2px solid #e8ecf1; white-space: nowrap; z-index: 1; }
.report-table .opening-row th { background: #eef2ff; color: #194173; font-weight: 700; font-size: 0.75rem; border-bottom: 2px solid #c7d2fe; position: sticky; top: 32px; z-index: 1; }
.report-table tbody td { padding: 8px 12px; border-bottom: 1px solid #f1f5f9; white-space: nowrap; }
.report-table tbody tr:hover { background-color: rgba(25, 65, 115, 0.03); }
.report-table tfoot .closing-row td { background: #f0fdf4; border-top: 2px solid #194173; color: #194173; padding: 10px 12px; font-size: 0.875rem; }
.empty-card { border: 1px solid #e8ecf1 !important; border-radius: 8px !important; box-shadow: 0 1px 3px rgba(0,0,0,0.04) !important; }
</style>
